// External
import join from 'url-join';
import { Map } from 'immutable';

// Internal
import { asyncAction, createAction, http } from '../util';
import { getSelectedDepartment } from '../shop/selectors';
import { getUserAccount, getUserData, accountDetails } from '../user/selectors';
import { formatSearchParams, formatFacetParams } from '../util/formatSearchParams';

// Constants
const api = join(import.meta.env.VITE_API_URL, 'v1');
const productServiceFacetValues = [
  'catalogs',
  'category',
  'color',
  'dimensions',
  'division',
  'gender',
  'segment',
  'silhouette',
  'silhouetteType',
  'size',
  'sport',
  'team'
];

// Actions
/**
 * @name clearProductsInfo
 * @description clear all products
 */
export const clearProductsInfo = createAction('CLEAR_PRODUCTS_INFO');

/**
 * @name fetchProductInfoAsync
 * @async
 * @description request product information and add to store
 * @param {Number} accountNumber
 * @param {String} [after]
 * @param {Number} [count]
 * @param {Number} deptNumber
 * @param {String} language
 * @param {String} orderType
 * @param {String} pricelistType
 * @param {String} [productCode]
 * @param {String} [productName]
 * @param {String} region
 * @param {Map} [selectedFacets]
 * @param {Array} [selectedFilters]
 * @param {String} [sortDirection]
 * @param {String} [sortField]
 * @param {Array} styleNumbers
 * @param {Number} [wholesaleMax]
 * @param {Number} [wholesaleMin]
 */
export const fetchProductInfoAsync = asyncAction({
  baseName: 'FETCH_PRODUCT_INFO',
  action: action => ({
    accountNumber,
    after,
    deptNumber,
    language,
    orderType,
    pricelistType,
    productCode,
    productName,
    region,
    selectedFacets = Map({}),
    selectedFilters = [],
    sortDirection,
    sortField,
    styleNumbers = [],
    count = styleNumbers.length,
    wholesaleMax,
    wholesaleMin
  }) => dispatch => {
    dispatch(action.start(styleNumbers));

    const productQuery = [after ? `&after=${after}` : ''];
    const aggregationValues = ['wholesale_min', 'wholesale_max'];
    const facetFilterSearch = [
      formatFacetParams(selectedFacets.toObject()),
      formatSearchParams(selectedFilters),
      productCode ? `&productCode=${productCode}` : '',
      productName ? `&productName=${productName}` : '',
      styleNumbers ? `&styleNumbers=${styleNumbers}` : '',
      wholesaleMin ? `&wholesaleMin=${wholesaleMin}` : '',
      wholesaleMax ? `&wholesaleMax=${wholesaleMax}` : '',
      sortField ? `&sortField=${sortField}` : '',
      sortDirection ? `&sortDirection=${sortDirection}` : ''
    ];

    const req = join(
      `${productQuery.join('')}`,
      `&aggregations=${aggregationValues.join(',')}`,
      `&facets=${productServiceFacetValues.join(',')}${facetFilterSearch.join('')}`,
      `&region=${region}&pricelistType=${pricelistType}&language=${language}`
    );

    return http
      .get(join(api, `accounts/${accountNumber}/departments/${deptNumber}/catalogs/${orderType}?count=${count}`, req))
      .then(({ data: response }) => {
        let updatedResponse = {};

        // If service returns products, fill out the response
        if (!(Array.isArray(response) && response.length === 0)) {
          updatedResponse = { ...response };
        }
        return dispatch(action.success({ products: updatedResponse, requestedStyles: styleNumbers }));
      })
      .catch(error => dispatch(action.error({ error, styleNumbers })));
  }
});

/**
 * @name fetchProductInfo
 * @async
 * @description dispatch request product information and add to store
 * @param {Number} [accountNumber]
 * @param {String} [after]
 * @param {Number} [count]
 * @param {Number} [deptNumber]
 * @param {String} [language]
 * @param {String} [orderType]
 * @param {String} [pricelistType]
 * @param {String} [productCode]
 * @param {String} [productName]
 * @param {String} [region]
 * @param {Map} [selectedFacets]
 * @param {Array} [selectedFilters]
 * @param {String} [sortDirection]
 * @param {String} [sortField]
 * @param {Array} styleNumbers
 * @param {Number} [wholesaleMax]
 * @param {Number} [wholesaleMin]
 */
export const fetchProductInfo = ({ selectedFacets = Map({}), selectedFilters = [], styleNumbers = [], ...rest }) => (
  dispatch,
  getState
) => {
  // if no products, don't continue
  if (styleNumbers.length === 0) {
    return Promise.resolve();
  }

  const state = getState();
  const { number: departmentNumber, type: orderType } = getSelectedDepartment(state);
  const { number: accountNumber } = getUserAccount(state);
  const { locale } = getUserData(state);
  const { pricelistType, region } = accountDetails(state);

  const params = {
    accountNumber,
    deptNumber: departmentNumber,
    language: locale.split('_')[0].toUpperCase(),
    pricelistType,
    region,
    orderType,
    styleNumbers,
    selectedFacets,
    selectedFilters,
    ...rest
  };

  return dispatch(fetchProductInfoAsync(params));
};

export default {
  clearProductsInfo,
  fetchProductInfoAsync,
  fetchProductInfo
};
